import React, { useEffect, useState, useRef } from 'react'
import { Field, ErrorMessage } from 'formik'
import { useDispatch } from 'react-redux'
import { Dropdown, DropdownButton, FormControl, InputGroup } from 'react-bootstrap'

import CustomTooltip from 'components/CustomTooltip'
import { isWasmerFile } from 'utils/general'
import { showCreateSiteModal, showCustomDomainModal, showMessageModal } from 'Manage/actions'
import styles from './DomainInputForm.module.css'
import { useLocation } from 'react-router-dom'

const DomainInputForm = ({
  name,
  initialValues,
  userProfile,
  inPreviewMode,
  setFieldValue,
  uploadValues,
  updatingPhpFile,
  disabled
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const inputRef = useRef(null)
  const { domainSuffix, customDomains, tlds } = initialValues
  const { selectedFile } = uploadValues
  const { siteLimit } = userProfile
  const customDomainSuffix = localStorage.getItem('active_custom_domain')

  const searchParams = new URLSearchParams(location.search)
  const shouldEditLinkName = searchParams.get('edit_link_name') === 'true'
  const hasSelectedRef = useRef(false)
  const shouldMaskAndLockLinkName = !!inPreviewMode

  const onAddCustomDomain = () => {
    dispatch(showCreateSiteModal(false))

    if (siteLimit === customDomains.length) {
      dispatch(showMessageModal({ title: 'Limit reached', message: 'Custom domain limit reached' }))
    } else {
      dispatch(showCustomDomainModal(true))
    }
  }

  const isCustomDomain = (domainSuffix) => !tlds.includes(domainSuffix)

  const onSelectDomainSuffix = (eK, event) => {
    const selectedDomainSuffix = event.target.text
    setFieldValue(`${name}.domainSuffix`, selectedDomainSuffix)
    setFieldValue(`${name}.isCustomDomain`, isCustomDomain(selectedDomainSuffix))
  }

  useEffect(() => {
    if (!!customDomainSuffix) {
      const foundDomain = customDomains.find(
        (item) => `.${item.domain.toLowerCase()}` === customDomainSuffix.toLowerCase()
      )
      if (!!foundDomain && !tlds.includes(customDomainSuffix)) {
        setFieldValue(`${name}.isCustomDomain`, true)
        setFieldValue(`${name}.domainSuffix`, customDomainSuffix)
        setFieldValue(`${name}.subdomain`, localStorage.getItem('active_custom_subdomain'))
      }
    }
  }, [customDomainSuffix])

  useEffect(() => {
    if (isWasmerFile(selectedFile)) {
      setFieldValue(`${name}.domainSuffix`, '.tiiny.io')
    }
  }, [selectedFile])

  const handleBlur = (e) => {
    // Prevent Formik from clearing the error on blur
    e.preventDefault()
  }

  return (
    <>
      <InputGroup className={styles.inputContainer}>
        <InputGroup>
          {/** SUBDOMAIN TEXT INPUT */}
          <Field name={`${name}.subdomain`}>
            {({ field }) => (
              <FormControl
                {...field}
                type={shouldMaskAndLockLinkName ? 'password' : 'text'}
                placeholder="link-name"
                disabled={disabled || updatingPhpFile || shouldMaskAndLockLinkName}
                onBlur={handleBlur}
                onChange={(e) => {
                  setFieldValue(`${name}.subdomain`, e.target.value)
                }}
                className={styles.inputDomain}
                ref={(el) => {
                  inputRef.current = el

                  // Run select/focus once
                  if (el && shouldEditLinkName && !shouldMaskAndLockLinkName && !hasSelectedRef.current) {
                    hasSelectedRef.current = true
                    requestAnimationFrame(() => {
                      el.focus()
                      el.select()
                    })
                  }
                }}
              />
            )}
          </Field>
          {/** END SUBDOMAIN TEXT INPUT */}

          {/** DOMAIN TLD DROPDOWN */}
          <CustomTooltip label="To change the domain, please upload a new project">
            <DropdownButton
              id={styles['input-group-dropdown-2']}
              variant="outline-primary"
              as={InputGroup.Append}
              title={domainSuffix}
              value={domainSuffix}
              onSelect={onSelectDomainSuffix}
              disabled={true}
            >
              <>
                {tlds?.map((tld) => (
                  <Dropdown.Item key={tld} value={tld}>
                    {tld}
                  </Dropdown.Item>
                ))}

                {/** custom domains */}
                {!isCustomDomain(domainSuffix) &&
                  customDomains?.map((customDomain) => (
                    <Dropdown.Item
                      key={customDomain}
                      value={`.${customDomain.domain}`}
                      active={domainSuffix === `.${customDomain.domain}`}
                    >
                      .{customDomain.domain}
                    </Dropdown.Item>
                  ))}

                {/* Only show divider if the second section visible */}
                {!isCustomDomain(domainSuffix) && !isWasmerFile(selectedFile) && !!customDomains && (
                  <Dropdown.Divider />
                )}
              </>

              {!isCustomDomain(domainSuffix) && !isWasmerFile(selectedFile) && !!customDomains && (
                <>
                  <Dropdown.Item key={1} onClick={onAddCustomDomain}>
                    <span style={{ fontSize: '14px', color: '#333' }}>Add custom domain</span>
                  </Dropdown.Item>
                </>
              )}
            </DropdownButton>
          </CustomTooltip>

          {/** END DOMAIN TLD DROPDOWN */}
        </InputGroup>
      </InputGroup>

      {/** ERROR DOMAIN */}
      <ErrorMessage
        name={`${name}.subdomain`}
        render={() => (
          <div className="mt-1 validation-error text-center">
            {!isCustomDomain(domainSuffix) &&
              'Invalid link-name: Only letters, numbers, and hyphens/dashes are allowed.'}
            {isCustomDomain(domainSuffix) && 'Please enter a valid subdomain or "www"'}
            <CustomTooltip label="Please select a different link name for your site.">
              <span className="link ms-2">
                <u>Help</u>
              </span>
            </CustomTooltip>
          </div>
        )}
      />
      {/** END ERROR DOMAIN */}
    </>
  )
}

export default DomainInputForm
